import Phaser from 'phaser';
import * as THREE from 'three';
import { InputManager } from '../core/InputManager';
import { createSlime, SLIME_COLOR, type SlimeMesh } from '../character/Slime';
import { SlimeAnimator } from '../character/SlimeAnimator';
import { SlimeTrail } from '../character/SlimeTrail';
import { CharacterController, MOVEMENT } from '../character/CharacterController';
import {
  ThirdPersonCamera,
  type CameraSettings,
} from '../camera/ThirdPersonCamera';
import { ThreeSceneManager } from '../world/ThreeSceneManager';
import { SlimeRope } from '../interact/SlimeRope';
import { separateCrates, type Pushable } from '../interact/Pushable';
import { HUD } from '../ui/HUD';
import { PauseMenu } from '../ui/PauseMenu';
import { PhotoBar } from '../ui/PhotoBar';
import { AudioManager } from '../audio/AudioManager';
import { BUILD_TAG } from '../buildTag';
import { RemoteSlime } from '../game/RemoteSlime';
import { parseColorHex, type MultiSession, type Session } from '../session';
import { STAGES, formatTime, saveBest } from '../stages/stages';
import { STAGE_COUNT, clampStageIndex, type PlayerInfo } from '../../shared/protocol';
import type { FinishZone } from '../world/ThreeSceneManager';
import type { MovingPlatform, PlatformSupport } from '../world/CollisionWorld';
import { waterTimerTick } from '../world/CollisionWorld';
import { ChatUI } from '../ui/ChatUI';
import { AimFade } from '../game/AimFade';
import { MAX_RECONNECT_ATTEMPTS, reconnectDelayMs, type NetEvents } from '../net/NetClient';
import { getTurnstileToken } from '../net/turnstile';
import { StageRuntime } from '../stages/StageRuntime';

const WIN_BTN =
  'padding:10px 22px;font-size:15px;font-weight:700;color:#0a0a0f;' +
  'background:#a3e635;border:none;border-radius:10px;cursor:pointer;';
const WIN_GHOST =
  'padding:10px 22px;font-size:15px;color:#e8e8ef;background:transparent;' +
  'border:1px solid rgba(232,232,239,0.35);border-radius:10px;cursor:pointer;';
import { loadCameraSettings, saveCameraSettings, loadGraphicSettings, saveGraphicSettings, type GraphicSettings } from '../core/SettingsStore';

export class MainGameScene extends Phaser.Scene {
  private input3d!: InputManager;
  private controller!: CharacterController;
  private cameraRig!: ThirdPersonCamera;
  private cameraSettings: CameraSettings = loadCameraSettings();
  private world!: ThreeSceneManager;
  private threeRenderer!: THREE.WebGLRenderer;
  private threeCamera!: THREE.PerspectiveCamera;
  private hud!: HUD;
  private pauseMenu!: PauseMenu;
  private audioManager!: AudioManager;
  private trail!: SlimeTrail;
  private rope!: SlimeRope;
  /** Fade lingkaran objek/wall yang menghalangi garis bidik AIM. */
  private aimFade = new AimFade();
  private pushables: Pushable[] = [];
  private session: Session = { mode: 'single', stageIndex: 0 };
  private remotes = new Map<string, RemoteSlime>();
  /** Daftar pemain MP terbaru (hak host dibaca dari sini, bisa migrasi). */
  private mpPlayers: PlayerInfo[] = [];
  /** Echo pesan stage sendiri dari server: konsumsi sekali, jangan reload ulang. */
  private stageEcho = false;
  /** Auto-reconnect in-game: percobaan, timer, overlay, dan status tutup. */
  private reconnectAttempt = 0;
  private reconnectTimer = 0;
  private reconnectOverlay: HTMLDivElement | null = null;
  private netDead = false;
  /** Runtime progresi stage bergate (Stage 1 dkk). */
  private stageRuntime: StageRuntime | null = null;
  private netAcc = 0;
  private netInfoAcc = 0;
  private lastStateRx = -1;
  private stageIndex = 0;
  private finish: FinishZone[] = [];
  /** Jumlah pemain (1 = single; 2-4 = multiplayer, menambah crate ekstra). */
  private playerCount = 1;
  /** Titik aman terakhir (daratan kering) untuk respawn jatuh ke air. */
  private lastSafe = new THREE.Vector3();
  /** Timer sentuhan air (-1 = bebas). */
  private waterTimer = -1;
  private spawnPts: THREE.Vector3[] = [new THREE.Vector3(0, 0, 0)];
  private stageTime = 0;
  private stageHudAcc = 0;
  private prevAnimState = '';
  private won = false;
  private winOverlay: HTMLDivElement | null = null;
  private chat: ChatUI | null = null;
  private chatOpen = false;
  private onGlobalKey = (e: KeyboardEvent): void => {
    if (
      e.key === 'Enter' &&
      this.session.mode === 'multi' &&
      !this.paused &&
      !this.won &&
      !this.photoMode &&
      !this.chatOpen &&
      this.input3d.isPointerLocked
    ) {
      this.openChat();
    }
  };
  private photoMode = false;
  private photoBar: PhotoBar | null = null;
  private photoOrbitX = 0;
  private photoOrbitY = 0;
  private uiContainer!: HTMLElement;
  private blockerEl!: HTMLElement;
  private tmpRopeFrom = new THREE.Vector3();
  private tmpRopeTo = new THREE.Vector3();
  private tmpGrabDir = new THREE.Vector3();
  private tmpMarker = new THREE.Vector3();
  private tmpRopeN = new THREE.Vector3(0, 0, 1);
  private grabTime = 0;
  private slime!: SlimeMesh;
  private graphicSettings: GraphicSettings = loadGraphicSettings();

  private paused = false;
  private resizeHandler = () => this.onResize();
  private glLost = false;
  private onGlLost = (e: Event): void => {
    e.preventDefault();
    this.glLost = true;
  };
  private onGlRestored = (): void => {
    this.glLost = false;
  };

  constructor() {
    super('MainGameScene');
  }

  create(data?: { session?: Session }): void {
    this.session = data?.session ?? { mode: 'single', stageIndex: 0 };
    const canvas = document.getElementById('game-3d') as HTMLCanvasElement;
    const uiContainer = document.getElementById('game-ui') as HTMLDivElement;
    const blocker = document.getElementById('blocker') as HTMLDivElement;
    this.uiContainer = uiContainer;
    this.blockerEl = blocker;
    const buildTagEl = document.createElement('p');
    buildTagEl.style.cssText = 'font-size:11px;opacity:0.5;';
    buildTagEl.textContent = `v${BUILD_TAG}`;
    blocker.querySelector('.panel')?.appendChild(buildTagEl);

    this.input3d = new InputManager(canvas);

    this.threeRenderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.threeRenderer.shadowMap.enabled = true;
    this.threeRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Context WebGL bisa hilang (GPU reset/memori, Firefox): jangan
    // crash-kan loop, pulihkan otomatis saat browser mengembalikannya.
    canvas.addEventListener('webglcontextlost', this.onGlLost, false);
    canvas.addEventListener('webglcontextrestored', this.onGlRestored, false);

    this.world = new ThreeSceneManager();
    this.stageIndex = clampStageIndex(this.session.stageIndex);
    this.playerCount =
      this.session.mode === 'multi'
        ? Math.max(1, Math.min(4, this.session.players.length))
        : 1;
    const worldHandles = this.world.build(this.stageIndex, this.playerCount);
    this.pushables = worldHandles.pushables;
    this.finish = worldHandles.finish;
    this.spawnPts = worldHandles.spawnPts;

    const characterRoot = new THREE.Object3D();
    if (this.session.mode === 'multi') {
      const spawn = this.spawnPts[(this.session.spawns[this.session.selfId] ?? 0) % this.spawnPts.length];
      characterRoot.position.copy(spawn);
    } else {
      characterRoot.position.copy(this.spawnPts[0]);
    }
    this.lastSafe.copy(characterRoot.position);
    worldHandles.scene.add(characterRoot);

    const visualPivot = new THREE.Object3D();
    characterRoot.add(visualPivot);

    const slimeColorHex =
      this.session.mode === 'multi' ? parseColorHex(this.session.color) : SLIME_COLOR;
    const slime = createSlime(0.55, 0.9, slimeColorHex);
    visualPivot.add(slime.group);
    this.slime = slime;
    slime.setStyle(this.graphicSettings.slimeStyle);

    const animator = new SlimeAnimator(slime);

    this.threeCamera = new THREE.PerspectiveCamera(
      this.cameraSettings.fov,
      window.innerWidth / window.innerHeight,
      0.1,
      200,
    );

    this.trail = new SlimeTrail(worldHandles.scene, (x, z) => {
      const s = this.world.collisionWorld.sampleGroundHeight(x, z);
      let h = s.valid ? s.height : 0;
      // Puncak crate ikut dihitung agar jejak menempel di atas jembatan crate.
      for (const c of this.pushables) {
        if (Math.abs(x - c.pos.x) <= c.halfX && Math.abs(z - c.pos.z) <= c.halfZ) {
          h = Math.max(h, c.pos.y + c.height);
        }
      }
      return { height: h, normalX: -s.tiltX, normalY: 1, normalZ: -s.tiltZ };
    }, undefined, undefined, (x, z) => this.platformAt(x, z));
    this.trail.setMode(this.graphicSettings.trailMode);
    this.trail.setStyle(this.graphicSettings.slimeStyle);
    this.trail.setColor(slimeColorHex);

    this.rope = new SlimeRope(worldHandles.scene);
    this.rope.setStyle(this.graphicSettings.slimeStyle);
    this.rope.setColor(slimeColorHex);

    this.controller = new CharacterController(characterRoot, animator, {
      input: this.input3d,
      cameraYawProvider: () => this.cameraRig.currentYaw,
      collisionWorld: worldHandles.collisionWorld,
      pushables: this.pushables,
    });

    this.cameraRig = new ThirdPersonCamera(
      this.threeCamera,
      this.controller,
      worldHandles.colliders,
      this.cameraSettings,
    );
    // Crate ikut menahan kamera (by-ref, ref array stabil lintas loadStage).
    this.cameraRig.setDynamics(this.pushables);

    this.hud = new HUD(uiContainer);
    this.hud.setDebugVisible(this.graphicSettings.debugHud);
    this.refreshStageLabel();
    if (this.session.mode === 'multi') {
      this.hud.setRoomInfo(`ROOM ${this.session.code}`);
      this.setupMultiplayer(worldHandles.scene);
      // Chat di-game: overlay minimizable (minimized = pil CHAT + feed pasif histori).
      this.chat = new ChatUI(uiContainer, true, {
        onSend: (text) => {
          if (this.session.mode === 'multi') this.session.net.sendChat(text);
        },
        onOpenChange: (open) => {
          this.chatOpen = open;
          this.input3d.setSuspended(open);
          if (open) {
            if (this.input3d.isPointerLocked) this.input3d.exitPointerLock();
          } else {
            this.resume();
          }
        },
      });
      this.chat.bindKeys();
      window.addEventListener('keydown', this.onGlobalKey);
    }
    this.pauseMenu = new PauseMenu(uiContainer, this.cameraSettings, this.graphicSettings, {
      onResume: () => this.resume(),
      onStageSelect: (index) => this.pickStage(index),
      onQuitToMenu: () => {
        // Reset penuh via reload (konteks GL + koneksi net dibersihkan
        // total); server menghapus pemain saat socket tutup.
        if (this.session.mode === 'multi') this.session.net.close();
        window.location.reload();
      },
      onSettingsChange: (patch) => {
        this.cameraRig.applySettings(patch);
        saveCameraSettings(this.cameraSettings);
      },
      onRestartStage: () => this.restartStage(),
      onGraphicChange: (patch) => {
        Object.assign(this.graphicSettings, patch);
        if (patch.trailMode) this.trail.setMode(this.graphicSettings.trailMode);
        if (patch.slimeStyle) this.slime.setStyle(this.graphicSettings.slimeStyle);
        if (patch.trailMode || patch.slimeStyle) {
          this.trail.setStyle(this.graphicSettings.slimeStyle);
        }
        if (patch.slimeStyle) this.rope.setStyle(this.graphicSettings.slimeStyle);
        if (patch.debugHud !== undefined) this.hud.setDebugVisible(this.graphicSettings.debugHud);
        saveGraphicSettings(this.graphicSettings);
      },
    });

    this.audioManager = new AudioManager(this.sound);
    this.audioManager.register('footstep', 'footstep');
    this.audioManager.register('interactSwing', 'interact_swing');
    this.audioManager.register('interactHit', 'interact_hit');
    this.audioManager.register('jump', 'jump');
    this.audioManager.register('land', 'land');

    this.controller.stateMachine.onChange((_from, to) => {
      if (to === 'Jump') this.audioManager.play('jump');
      if (to === 'Land') this.audioManager.play('land');
    });

    document.addEventListener('pointerlockchange', () => {
      // Mode foto keluar dari lock dengan sengaja: jangan tampilkan pause.
      if (this.photoMode) return;
      // Overlay finish/win sengaja melepas lock: jangan tumpuk pause menu.
      if (this.won) return;
      // Chat membuka input: kunci pointer dilepas sementara, bukan pause.
      if (this.chatOpen) return;
      if (this.input3d.isPointerLocked) {
        blocker.classList.add('hidden');
        this.pauseMenu.hide();
        this.paused = false;
        this.hud.setCrosshairVisible(true);
      } else {
        this.paused = true;
        this.pauseMenu.setStagePicker(this.canPickStage(), this.stageIndex);
        this.pauseMenu.show();
        this.hud.setCrosshairVisible(false);
      }
    });

    if (!InputManager.isPointerLockSupported()) {
      blocker.querySelector('p')!.textContent =
        'Browser tidak mendukung Pointer Lock. Gunakan browser desktop (Chrome/Edge/Firefox).';
      blocker.style.cursor = 'default';
    } else {
      blocker.addEventListener('click', () => this.input3d.requestPointerLock());
    }

    window.addEventListener('resize', this.resizeHandler);
    this.onResize();
    this.initStageRuntime();
  }

  /** Runtime progresi bergate untuk stage yang mendefinisikan `gates`. */
  private initStageRuntime(): void {
    const def = STAGES[this.stageIndex];
    if (def.gates === undefined && def.checkpoints === undefined) {
      this.stageRuntime = null;
      return;
    }
    this.stageRuntime = new StageRuntime(def, {
      world: this.world.collisionWorld,
      player: () => ({
        x: this.controller.position.x,
        y: this.controller.position.y,
        z: this.controller.position.z,
        grounded: this.controller.isGrounded,
        state: this.controller.getState(),
      }),
      pushables: this.pushables,
      setGateOpen: (i) => this.world.setGateOpen(i),
      resetPlayer: (x, z) => {
        const gy = this.world.collisionWorld.sampleGroundHeight(x, z);
        const s = this.world.collisionWorld.platformList.find(
          (pl) => Math.abs(pl.px - x) <= pl.spec.w / 2 && Math.abs(pl.pz - z) <= pl.spec.d / 2,
        );
        const y = gy.valid && !s ? Math.max(gy.height, 0) : 0;
        this.controller.resetTo(x, y, z);
        this.lastSafe.set(x, y, z);
        this.trail.clear();
        this.rope.hide();
      },
      respawnPushable: (i, x, z) => {
        const p = this.pushables[i];
        if (!p) return;
        const gy = this.world.collisionWorld.sampleGroundHeight(x, z);
        p.pos.set(x, gy.valid ? gy.height : 0, z);
        p.vel.set(0, 0, 0);
        p.clearAnchor();
        p.syncMesh();
      },
      chase: () =>
        this.world.movingPlatforms.find((pl) => pl.spec.mode === 'chase') ?? null,
      inFinishZone: (x, z) => this.finish.some((fz) => this.inFinishZone(fz, x, z)),
      crateSpeed: (i) => Math.hypot(this.pushables[i].vel.x, this.pushables[i].vel.z),
      hint: (text) => this.hud.showHint(text),
      objective: (text) => this.hud.setObjective(text),
    });
  }

  private openChat(): void {
    if (this.chatOpen || !this.chat) return;
    // ChatUI.open() memicu onOpenChange(true) -> suspend + unlock.
    this.chat.open();
  }

  private resume(): void {
    this.input3d.requestPointerLock();
  }

  private setPhotoMode(on: boolean): void {
    if (on === this.photoMode) return;
    this.photoMode = on;
    if (on) {
      this.hud.setPhotoMode(true);
      this.pauseMenu.hide();
      this.photoBar = new PhotoBar(this.uiContainer, {
        onOrbit: (dx, dy) => {
          this.photoOrbitX = THREE.MathUtils.clamp(this.photoOrbitX + dx, -400, 400);
          this.photoOrbitY = THREE.MathUtils.clamp(this.photoOrbitY + dy, -400, 400);
        },
        onZoom: (deltaY) => {
          const next = THREE.MathUtils.clamp(
            this.cameraRig.baseDistance + Math.sign(deltaY) * 0.5,
            1.5,
            10,
          );
          this.cameraRig.applySettings({ distance: next });
        },
        onCapture: () => this.capturePhoto(),
        onExit: () => this.setPhotoMode(false),
      });
      this.input3d.exitPointerLock();
    } else {
      this.photoBar?.dispose();
      this.photoBar = null;
      this.hud.setPhotoMode(false);
      // Kembali lewat alur resume normal: klik blocker untuk lock lagi.
      this.blockerEl.classList.remove('hidden');
    }
  }

  /** Render aman: lewati bila konteks WebGL hilang (anti-crash loop). */
  private render(): void {
    if (!this.glLost) this.threeRenderer.render(this.world.scene, this.threeCamera);
  }

  private capturePhoto(): void {
    // Render + readback dalam task yang sama (tanpa preserveDrawingBuffer).
    this.render();
    this.threeRenderer.domElement.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `slime-brawler-${Date.now()}.png`;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    });
  }

  /** Titik spawn MP ke-i (dipetakan ke spawn stage). */
  private mpSpawn(index: number): THREE.Vector3 {
    return this.spawnPts[((index % this.spawnPts.length) + this.spawnPts.length) % this.spawnPts.length];
  }

  /** Teleport ke stage lain: bangun ulang layout + reset pemain/efek/timer. */
  private loadStage(index: number): void {
    this.stageIndex = clampStageIndex(index);
    const layout = this.world.buildStageLayout(this.stageIndex, this.playerCount);
    this.finish = layout.finish;
    this.spawnPts = layout.spawnPts;
    // this.pushables menunjuk array manager yang sama (dipertahankan ref).
    const s0 = this.spawnPts[0];
    this.controller.resetTo(s0.x, s0.y, s0.z);
    this.lastSafe.copy(s0);
    this.trail.clear();
    this.rope.hide();
    // Mesh lama dibuang -> lepas semua fade (state dibangun ulang).
    this.aimFade.dispose();
    this.stageTime = 0;
    this.prevAnimState = '';
    this.won = false;
    this.hideWin();
    this.refreshStageLabel();
    this.initStageRuntime();
  }

  /** Air bahaya: kaki menyentuh permukaan sungai (tanpa pijakan crate
   *  /objek di atasnya) -> cipratan + jeda 0.25 dtk tenggelam -> respawn
   *  ke titik aman terakhir + jejak dibersihkan. Kamera ikut glide halus
   *  (tanpa snap) karena respawn tak memicu teleport kamera. */
  private updateWaterRespawn(delta: number): void {
    const p = this.controller.position;
    const wy = this.world.collisionWorld.waterAt(p.x, p.z);
    const touching = wy !== null && p.y < wy + 0.1;
    const st = waterTimerTick(this.waterTimer, touching, delta);
    this.waterTimer = st.timer;
    if (st.splash && wy !== null) {
      this.trail.splash(p.x, wy, p.z, 0.9);
    }
    if (st.respawn) {
      this.controller.resetTo(this.lastSafe.x, this.lastSafe.y, this.lastSafe.z);
      this.trail.clear();
      this.rope.hide();
      return;
    }
    if (this.controller.isGrounded && !touching) this.lastSafe.copy(p);
  }

  private inFinishZone(fz: FinishZone, x: number, z: number): boolean {
    return Math.hypot(x - fz.x, z - fz.z) < fz.r;
  }

  /** Platform bergerak yang berada PERSIS di bawah titik (permukaan top =
   *  tinggi sampel): dipakai jejak untuk menempel ke platform. */
  private platformAt(x: number, z: number): MovingPlatform | null {
    const base = this.world.collisionWorld.sampleGroundHeight(x, z);
    for (const pl of this.world.collisionWorld.platformList) {
      const inFoot =
        Math.abs(x - pl.px) <= pl.spec.w / 2 + 0.02 &&
        Math.abs(z - pl.pz) <= pl.spec.d / 2 + 0.02;
      if (inFoot && Math.abs(base.height - pl.spec.y) < 0.08) return pl;
    }
    return null;
  }

  /** Chip detik hingga arah platform bergerak berbalik (S7-S9). */
  private updatePlatformChip(pl: MovingPlatform, px: number, pz: number, py: number): void {
    void py;
    const { period, phase } = pl.spec;
    const u = (((pl.clock / period + (phase ?? 0.5)) % 1) + 1) % 1;
    if (Math.hypot(px - pl.px, pz - pl.pz) > 12) {
      this.hud.setPlatformChip('');
      return;
    }
    const secs = u < 0.5 ? (0.5 - u) * period : (1 - u) * period;
    const isTimedBridge = this.stageIndex === 7 || this.stageIndex === 8;
    const text = isTimedBridge
      ? `${u >= 0.5 ? 'MEMBUKA' : 'MENUTUP'} · ${secs.toFixed(1)}s`
      : `${secs.toFixed(1)}s`;
    this.hud.setPlatformChip(`PLATFORM ${text}`);
  }

  private checkFinish(): boolean {
    if (this.finish.length === 0) return false;
    if (this.finish[0].needCrate) {
      // Semua pad harus terisi crate (stage multi-finish butuh semua).
      return this.finish.every((fz) =>
        this.pushables.some((c) => this.inFinishZone(fz, c.pos.x, c.pos.z)),
      );
    }
    const p = this.controller.position;
    return this.finish.some((fz) => this.inFinishZone(fz, p.x, p.z));
  }

  private refreshStageLabel(): void {
    const def = STAGES[this.stageIndex];
    this.stageBaseLabel = `STAGE ${this.stageIndex + 1}/${STAGE_COUNT} · ${def.name}`;
    this.hud.setStageLabel(`${this.stageBaseLabel} · ${formatTime(this.stageTime)}`);
  }

  private stageBaseLabel = '';

  private onWin(): void {
    this.won = true;
    const isRecord = saveBest(this.stageIndex, this.stageTime);
    this.paused = true;
    // Lepas pointer lock: overlay tombol butuh kursor bebas (tanpa ini
    // kursor terkunci, overlay tak bisa diklik wajar + invisible).
    if (this.input3d.isPointerLocked) this.input3d.exitPointerLock();
    const def = STAGES[this.stageIndex];
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:absolute;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(10,10,15,0.72);pointer-events:auto;font-family:system-ui,sans-serif;';
    const last = this.stageIndex >= STAGE_COUNT - 1;
    const primary =
      this.session.mode === 'multi'
        ? `<button data-w="again" style="${WIN_BTN}">TETAP MAIN</button>`
        : last
          ? `<button data-w="menu" style="${WIN_BTN}">PILIH STAGE</button>`
          : `<button data-w="next" style="${WIN_BTN}">LANJUT</button>` +
            `<button data-w="retry" style="${WIN_GHOST}">ULANGI</button>`;
    overlay.innerHTML =
      '<div style="text-align:center;color:#e8e8ef;">' +
      `<div style="font-size:13px;letter-spacing:3px;color:#a3e635;">STAGE CLEAR!</div>` +
      `<h2 style="letter-spacing:2px;margin:4px 0;">${def.name}</h2>` +
      `<p style="opacity:0.75;">Waktu: ${formatTime(this.stageTime)}${isRecord ? ' · Rekor baru!' : ''}</p>` +
      `<div style="display:flex;gap:10px;justify-content:center;margin-top:14px;">${primary}` +
      `<button data-w="menu" style="${WIN_GHOST}">MENU</button>` +
      '</div></div>';
    const act = (ev: Event): void => {
      const b = (ev.target as HTMLElement).closest('button');
      if (!b) return;
      const w = b.getAttribute('data-w');
      if (w === 'menu') {
        window.location.reload();
        return;
      }
      if (w === 'retry' && this.session.mode !== 'multi') {
        this.loadStage(this.stageIndex);
      } else if (w === 'next' && this.session.mode !== 'multi') {
        this.loadStage(this.stageIndex + 1);
      }
      this.hideWin();
      this.paused = false;
      // Lanjut main: kunci pointer lagi (anggota overlay win adalah
      // user-gesture, requestPointerLock diizinkan browser).
      this.resume();
    };
    overlay.addEventListener('click', act);
    this.uiContainer.appendChild(overlay);
    this.winOverlay = overlay;
  }

  private hideWin(): void {
    this.winOverlay?.remove();
    this.winOverlay = null;
  }

  private setupMultiplayer(scene: THREE.Scene): void {
    if (this.session.mode !== 'multi') return;
    const session = this.session;
    this.mpPlayers = [...session.players];
    for (const p of session.players) {
      if (p.id !== session.selfId) this.addRemote(scene, p, session);
    }
    // Pakai koneksi lobby yang sama (jangan connect ulang = member ganda).
    session.net.setEvents(this.buildNetEvents(scene, session));
  }

  /** Handler socket in-game; dipakai juga saat reconnect (objek sama). */
  private buildNetEvents(scene: THREE.Scene, session: MultiSession): NetEvents {
    return {
      onOpen: () => undefined,
      onWelcome: (selfId, _started, spawns, players, stage) =>
        this.onWelcomeResync(session, selfId, spawns, players, stage),
      onPlayers: (players) => {
        this.mpPlayers = players;
        this.syncRemotes(scene, session, players);
      },
      onStage: (index) => this.onRemoteStage(index),
      onStart: () => undefined,
      onState: (id, x, y, z, yaw, anim) => {
        this.lastStateRx = this.grabTime;
        this.remotes.get(id)?.setTarget(x, y, z, yaw, anim);
      },
      onBye: (id) => this.removeRemote(scene, id),
      onChat: (_id, name, text) => this.chat?.append(name, text),
      onError: (message) => this.hud.setRoomInfo(`ROOM ${session.code} — ${message}`),
      onClose: () => this.scheduleGameReconnect(session),
    };
  }

  /** Welcome (ulang) di dalam game: samakan identitas + stage tanpa reset posisi. */
  private onWelcomeResync(
    session: MultiSession,
    selfId: string,
    spawns: MultiSession['spawns'],
    players: PlayerInfo[],
    stage: number,
  ): void {
    if (this.netDead || this.session.mode !== 'multi') return;
    this.reconnectAttempt = 0;
    this.hideGameReconnect();
    session.selfId = selfId;
    session.spawns = spawns;
    session.players = players;
    this.mpPlayers = players;
    this.stageEcho = false;
    const index = clampStageIndex(stage);
    if (index !== this.stageIndex) {
      this.loadStage(index);
      this.moveToOwnSpawn();
    } else {
      this.syncRemotes(this.world.scene, session, players);
      this.hud.setRoomInfo(`ROOM ${session.code} — ${this.remotes.size + 1} pemain`);
    }
  }

  /** Putus di tengah game: overlay + coba lagi otomatis dengan kode sama. */
  private scheduleGameReconnect(session: MultiSession): void {
    if (this.netDead || this.session.mode !== 'multi') return;
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      this.hideGameReconnect();
      this.showDisconnect(session);
      return;
    }
    const wait = reconnectDelayMs(this.reconnectAttempt) + Math.floor(Math.random() * 500);
    this.showGameReconnect(`KONEKSI PUTUS — menyambung ulang… (${this.reconnectAttempt + 1})`);
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => void this.redialGame(session), wait);
  }

  private async redialGame(session: MultiSession): Promise<void> {
    if (this.netDead || this.session.mode !== 'multi') return;
    this.reconnectAttempt++;
    let token = '';
    try {
      const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined ?? '').trim();
      token = await getTurnstileToken(siteKey);
    } catch {
      // Tanpa token pun coba (server yang menilai).
    }
    if (this.netDead || this.session.mode !== 'multi') return;
    session.net.connect(session.serverUrl, session.code, this.buildNetEvents(this.world.scene, session), token || undefined);
  }

  private showGameReconnect(text: string): void {
    if (!this.reconnectOverlay) {
      const overlay = document.createElement('div');
      overlay.style.cssText =
        'position:absolute;left:50%;top:14px;transform:translateX(-50%);z-index:6;' +
        'padding:8px 16px;font-family:system-ui,sans-serif;font-size:13px;color:#ffd166;' +
        'background:rgba(10,10,15,0.85);border:1px solid rgba(255,209,102,0.5);' +
        'border-radius:10px;pointer-events:none;';
      this.uiContainer.appendChild(overlay);
      this.reconnectOverlay = overlay;
    }
    this.reconnectOverlay.textContent = text;
  }

  private hideGameReconnect(): void {
    this.reconnectOverlay?.remove();
    this.reconnectOverlay = null;
  }

  /** Host MP boleh memilih stage (single tak perlu: ada alur LANJUT/ULANGI). */
  private canPickStage(): boolean {
    const session = this.session;
    if (session.mode !== 'multi') return false;
    return this.mpPlayers.find((p) => p.id === session.selfId)?.host === true;
  }

  /** Host mengganti stage dari pause: siarkan + muat lokal (echo diabaikan). */
  private pickStage(index: number): void {
    if (this.session.mode !== 'multi' || !this.canPickStage()) return;
    this.stageEcho = true;
    this.session.net.sendStage(index);
    this.loadStage(index);
    this.moveToOwnSpawn();
    this.pauseMenu.setStagePicker(true, this.stageIndex);
  }

  /** Stage pilihan host (server broadcast): muat + mulai dari spawn sendiri. */
  private onRemoteStage(index: number): void {
    if (this.session.mode !== 'multi') return;
    if (this.stageEcho) {
      this.stageEcho = false;
      return;
    }
    this.loadStage(index);
    this.moveToOwnSpawn();
  }

  /** Spawn milik sendiri (anti-numpuk di spawn 0 saat ganti stage MP). */
  private moveToOwnSpawn(): void {
    if (this.session.mode !== 'multi') return;
    const s = this.mpSpawn(this.session.spawns[this.session.selfId] ?? 0);
    this.controller.resetTo(s.x, s.y, s.z);
    this.lastSafe.copy(s);
  }

  private addRemote(scene: THREE.Scene, p: PlayerInfo, session: MultiSession): void {
    if (this.remotes.has(p.id) || p.id === session.selfId) return;
    const remote = new RemoteSlime(p.id, p.name, parseColorHex(p.color));
    const spawn = this.mpSpawn(session.spawns[p.id] ?? 0);
    remote.setTarget(spawn.x, spawn.y, spawn.z, 0, 'Idle');
    scene.add(remote.group);
    this.remotes.set(p.id, remote);
    this.hud.setRoomInfo(`ROOM ${session.code} — ${this.remotes.size + 1} pemain`);
  }

  private removeRemote(scene: THREE.Scene, id: string): void {
    const remote = this.remotes.get(id);
    if (!remote) return;
    this.remotes.delete(id);
    remote.dispose(scene);
    if (this.session.mode === 'multi') {
      this.hud.setRoomInfo(`ROOM ${this.session.code} — ${this.remotes.size + 1} pemain`);
    }
  }

  private syncRemotes(scene: THREE.Scene, session: MultiSession, players: PlayerInfo[]): void {
    const ids = new Set(players.map((p) => p.id));
    for (const [id] of this.remotes) {
      if (!ids.has(id)) this.removeRemote(scene, id);
    }
    for (const p of players) {
      const existing = this.remotes.get(p.id);
      if (existing) {
        existing.setName(p.name);
        existing.setColor(parseColorHex(p.color));
      } else {
        this.addRemote(scene, p, session);
      }
    }
  }

  private showDisconnect(session: MultiSession): void {
    void session;
    if (document.getElementById('mp-disconnect')) return;
    const overlay = document.createElement('div');
    overlay.id = 'mp-disconnect';
    overlay.style.cssText =
      'position:absolute;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(10,10,15,0.8);pointer-events:auto;font-family:system-ui,sans-serif;';
    overlay.innerHTML =
      '<div style="text-align:center;color:#e8e8ef;">' +
      '<h2 style="letter-spacing:2px;">KONEKSI PUTUS</h2>' +
      '<p style="opacity:0.7;margin:8px 0 14px;">Server multiplayer tidak terjangkau.</p>' +
      '<button id="mp-back" style="padding:10px 22px;font-size:15px;font-weight:700;color:#0a0a0f;background:#5dffd4;border:none;border-radius:10px;cursor:pointer;">KEMBALI KE MENU</button>' +
      '</div>';
    document.getElementById('app')?.appendChild(overlay);
    document.getElementById('mp-back')?.addEventListener('click', () => window.location.reload());
  }

  /** Tombol RESET di pause: ulangi stage aktif dari awal. */
  private restartStage(): void {
    this.pauseMenu.hide();
    this.hideWin();
    this.loadStage(this.stageIndex);
    this.paused = false;
    // Klik tombol = user-gesture: requestPointerLock diizinkan browser.
    this.resume();
  }

  update(_time: number, deltaMs: number): void {
    // Klem delta: hitch/pause (tab, GC, HMR) tanpa ini = teleport fisika
    // + kamera sekaligus (lompat maju-mundur sekejap).
    const delta = Math.min(deltaMs / 1000, 1 / 20);

    if (this.paused) {
      this.cameraRig.update(delta);
      this.render();
      return;
    }

    if (this.input3d.wasPressed('photo')) {
      this.setPhotoMode(!this.photoMode);
    }

    const mouse = this.input3d.consumeMouseDelta();
    if (this.photoMode) {
      // Dunia TETAP jalan normal; orbit seret + zoom hanya menggerakkan
      // kamera (HUD tetap sembunyi).
      this.cameraRig.applyMouseDelta(mouse.x + this.photoOrbitX, mouse.y + this.photoOrbitY);
      this.photoOrbitX = 0;
      this.photoOrbitY = 0;
    } else {
      this.cameraRig.applyMouseDelta(mouse.x, mouse.y);
    }

    this.cameraRig.update(delta);

    // Platform bergerak: carry dua-tahap (player + crate yang bertumpu
    // ikut meluncur), baru fisika normal menyesuaikan.
    const platforms = this.world.collisionWorld.platformList;
    let carryX = 0;
    let carryZ = 0;
    if (platforms.length > 0) {
      const p = this.controller.position;
      const supports: PlatformSupport[] = [
        {
          x: p.x,
          z: p.z,
          y: p.y,
          apply: (dx, dz) => {
            p.x += dx;
            p.z += dz;
            carryX += dx;
            carryZ += dz;
          },
        },
        ...this.pushables.map((c) => ({
          x: c.pos.x,
          z: c.pos.z,
          y: c.pos.y,
          apply: (dx: number, dz: number) => {
            c.pos.x += dx;
            c.pos.z += dz;
          },
        })),
      ];
      this.world.collisionWorld.stepPlatforms(delta, supports);
      this.updatePlatformChip(platforms[0], p.x, p.z, p.y);
    } else {
      this.hud.setPlatformChip('');
    }

    this.controller.update(delta);

    for (const crate of this.pushables) crate.integrate(delta, this.world.collisionWorld);
    for (let i = 0; i < this.pushables.length; i++) {
      for (let j = i + 1; j < this.pushables.length; j++) {
        separateCrates(this.pushables[i], this.pushables[j]);
      }
    }
    this.world.updateFinishVisual(this.pushables, delta);

    // Air bahaya: sentuh permukaan sungai tanpa pijakan -> respawn ke
    // titik aman terakhir (crate yang mengapung = pijakan sah).
    this.updateWaterRespawn(delta);

    // Timer + cek finish stage.
    if (!this.won) {
      this.stageTime += delta;
      this.stageHudAcc += delta;
      if (this.stageHudAcc >= 0.5) {
        this.stageHudAcc = 0;
        this.hud.setStageLabel(`${this.stageBaseLabel} · ${formatTime(this.stageTime)}`);
      }
      if (this.stageRuntime?.ownsStage) {
        this.stageRuntime.update(delta);
        if (this.stageRuntime.win) this.onWin();
      } else if (this.checkFinish()) {
        this.onWin();
      }
    }

    const grounding = this.controller.isGrounded;
    const p = this.controller.position;
    const animState = this.controller.getState();
    // Jejak melebar 1.2x diameter slime saat melompat/jatuh.
    const spread = animState === 'Jump' || animState === 'Fall' ? 1.2 : 1;
    this.trail.setWidth(this.slime.body.scale.x * this.slime.baseRadius * 2);
    this.trail.update(delta, p.x, p.y, p.z, grounding, carryX, carryZ, spread);

    // Cipratan mendarat: makin tinggi jatuhnya, makin besar radius.
    // fallHeight dalam meter; basis = radius badan.
    if (animState === 'Land' && this.prevAnimState !== 'Land') {
      const base = this.slime.body.scale.x * this.slime.baseRadius;
      const fall = Math.max(0, this.controller.fallHeight);
      const radius = base * Math.min(3.5, 0.9 + 0.35 * fall);
      this.trail.splash(p.x, p.y, p.z, Math.max(0.35, radius));
    }
    this.prevAnimState = animState;

    const wc = this.world.collisionWorld.sampleWall(p.x, p.y, p.z, 0.55, 0.9);
    if (wc) {
      this.trail.addWall(wc.x, wc.y, wc.z, wc.normalX, wc.normalY, wc.normalZ, grounding);
    } else {
      this.trail.addWall(p.x, p.y, p.z, 0, 1, 0, false);
    }

    // Tali karet slime <-> crate + indikator grab.
    this.grabTime += delta;
    const grabbed = this.controller.getGrabbed();
    if (grabbed) {
      // Titik + normal tempel dikunci controller saat grab mulai (frame
      // lokal crate): ikut yaw/lean, selalu rata di permukaan.
      grabbed.anchorWorld(this.tmpRopeTo, this.tmpRopeN);
      this.ropeStartInto(this.tmpRopeFrom, this.tmpRopeTo);
      const dist = Math.hypot(
        grabbed.pos.x - p.x,
        grabbed.pos.z - p.z,
      );
      const strain = THREE.MathUtils.clamp(
        (dist - this.controller.getGrabDist()) / (MOVEMENT.grabMaxDist - this.controller.getGrabDist()),
        0,
        1,
      );
      this.rope.link(this.tmpRopeFrom, this.tmpRopeTo, strain, this.grabTime, this.tmpRopeN, delta);
      this.rope.mark(null, this.grabTime);
      this.hud.setGrabPrompt(false);
    } else {
      this.rope.hide();
      const candidate = this.controller.getGrabCandidate();
      if (candidate) {
        this.tmpMarker.set(
          candidate.pos.x,
          candidate.pos.y + candidate.height + 0.45,
          candidate.pos.z,
        );
        this.rope.mark(this.tmpMarker, this.grabTime);
        this.hud.setGrabPrompt(true);
      } else {
        this.rope.mark(null, this.grabTime);
        this.hud.setGrabPrompt(false);
      }
    }

    this.render();

    // Fade lingkaran: objek/wall di garis bidik crosshair dibuat
    // tembus pandang di sekitar titik aim (setelah kamera final).
    this.aimFade.update(this.threeCamera, this.world.colliders, this.pushables);

    this.hud.update(delta, this.controller.getState());
    this.hud.setStamina(this.controller.getStamina());
    for (const remote of this.remotes.values()) remote.update(delta);
    this.broadcastState(delta);
    this.input3d.endFrame();
  }

  /** Kirim posisi lokal 15x/detik saat multiplayer. */
  private broadcastState(delta: number): void {
    if (this.session.mode !== 'multi') return;
    this.netAcc += delta;
    if (this.netAcc >= 1 / 15) {
      this.netAcc = 0;
      const net = this.session.net;
      if (net.isOpen) {
        const p = this.controller.position;
        net.sendState(p.x, p.y, p.z, this.controller.root.rotation.y, this.controller.getState());
      }
    }
    this.netInfoAcc += delta;
    if (this.netInfoAcc >= 0.5) {
      this.netInfoAcc = 0;
      const net = this.session.net;
      const age = this.lastStateRx < 0 ? '—' : `${(this.grabTime - this.lastStateRx).toFixed(1)}s`;
      this.hud.setNetInfo(
        `net ${net.isOpen ? 'ON' : 'OFF'} · remote ${this.remotes.size} · rx ${age}`,
      );
    }
  }

  /**
   * Titik awal tali: sedikit di dalam kulit badan menghadap crate ("dahi"
   * slime ke arah grab), diukur dari vertex terdeformasi frame berjalan.
   * Segmen interior gugur uji depth (lihat SlimeRope) -> tak ada rubber
   * tampak seperti jejak di dalam slime transparan, walau squash/wobble.
   * Gundukan weld menutup sambungan. Sengaja TIDAK memakai posisi
   * kamera supaya titik tempel tidak bergeser saat kamera diputar. Logika
   * targeting tetap pakai tengah badan.
   */
  private ropeStartInto(out: THREE.Vector3, target: THREE.Vector3): THREE.Vector3 {
    const p = this.controller.position;
    this.tmpGrabDir.subVectors(target, p);
    this.tmpGrabDir.y = 0;
    if (this.tmpGrabDir.lengthSq() > 1e-6) this.tmpGrabDir.normalize();
    else {
      const yaw = this.controller.root.rotation.y;
      this.tmpGrabDir.set(Math.sin(yaw), 0, Math.cos(yaw));
    }
    const r = this.slime.surfaceRadius(this.tmpGrabDir.x, this.tmpGrabDir.z, 0.55);
    // Jangkar 0.04 DI DALAM kulit: tube menembus dinding kontinu (tanpa
    // celah saat squash ekstrem) — segmen interior otomatis gugur uji
    // depth (tali renderOrder 2, badan 1 + depthWrite) jadi tak terlihat
    // dari sudut mana pun. Weld menutupi persilangan di luar.
    const rr = Math.max(0.1, r - 0.04);
    return out.set(
      p.x + this.tmpGrabDir.x * rr,
      p.y + 0.55,
      p.z + this.tmpGrabDir.z * rr,
    );
  }
  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.threeRenderer.setSize(w, h, false);
    this.threeCamera.aspect = w / h;
    this.threeCamera.updateProjectionMatrix();
  }

  shutdown(): void {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('keydown', this.onGlobalKey);
    const canvas = this.threeRenderer?.domElement;
    if (canvas) {
      canvas.removeEventListener('webglcontextlost', this.onGlLost);
      canvas.removeEventListener('webglcontextrestored', this.onGlRestored);
    }
    this.hideWin();
    this.chat?.dispose();
    this.chat = null;
    this.photoBar?.dispose();
    this.photoBar = null;
    for (const remote of this.remotes.values()) remote.dispose(this.world.scene);
    this.remotes.clear();
    // Matikan reconnect: scene dibuang (net.close melepaskan handler dulu).
    this.netDead = true;
    window.clearTimeout(this.reconnectTimer);
    this.hideGameReconnect();
    if (this.session.mode === 'multi') this.session.net.close();
    this.input3d.dispose();
    this.hud.dispose();
    this.pauseMenu.dispose();
    this.audioManager.dispose();
    this.trail.dispose();
    this.rope.dispose();
    this.aimFade.dispose();
    this.threeRenderer.dispose();
  }
}